"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Comedian } from "@/data/comedians";
import { UserComedianFollow } from "@/data/v2/client/user/follows";
import { logException } from "@/analytics/sentry";
import { unfollowComedianById } from "@punchup/data/client";
import { useUser } from "@punchup/data/legacy/hooks";

interface UnfollowComedianParams {
  comedian: Pick<Comedian, "id">;
  unfollowLocation?: string;
}

/**
 * Custom hook for unfollowing a comedian using React Query mutations
 *
 * Handles optimistic updates and automatic cache invalidation for user follows.
 * Uses the v2 API endpoint for unfollowing comedians.
 *
 * @returns Object containing:
 * - unfollowComedian: Mutation function to unfollow a comedian
 * - isUnfollowing: Whether the unfollow mutation is in progress
 * - error: Error object if mutation failed
 *
 * @example
 * ```tsx
 * const { unfollowComedian: unfollow, isUnfollowing } = useUnfollowComedian();
 *
 * const handleUnfollow = () => {
 *   unfollow({
 *     comedian: { id: 'comedian-123' },
 *     unfollowLocation: 'comedian-landing-page'
 *   });
 * };
 * ```
 */
export function useUnfollowComedian() {
  const queryClient = useQueryClient();
  const user = useUser();

  const mutation = useMutation({
    mutationFn: async ({
      comedian,
      unfollowLocation,
    }: UnfollowComedianParams) => {
      return unfollowComedianById(comedian.id, unfollowLocation);
    },
    onMutate: async ({ comedian }) => {
      await queryClient.cancelQueries({
        queryKey: ["user-comedian-follows", user?.id],
      });

      const previousFollows = queryClient.getQueryData<UserComedianFollow[]>([
        "user-comedian-follows",
        user?.id,
      ]);

      queryClient.setQueryData<UserComedianFollow[]>(
        ["user-comedian-follows", user?.id],
        (old) => {
          if (!old) return old;
          return old.filter((follow) => follow.comedian_id !== comedian.id);
        }
      );

      return { previousFollows };
    },
    onError: (error, variables, context) => {
      if (context?.previousFollows) {
        queryClient.setQueryData(
          ["user-comedian-follows", user?.id],
          context.previousFollows
        );
      }

      logException(error, {
        extra: {
          message: "Error unfollowing comedian",
          comedianId: variables.comedian.id,
          unfollowLocation: variables.unfollowLocation,
        },
      });
    },
    onSuccess: () => {
      // Analytics tracking is handled server-side in the v2 API
      queryClient.invalidateQueries({
        queryKey: ["user-comedian-follows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["user-content-access"],
      });
    },
  });

  return {
    unfollowComedian: mutation.mutate,
    unfollowComedianAsync: mutation.mutateAsync,
    isUnfollowing: mutation.isPending,
    error: mutation.error,
  };
}
